import React, { useState, useEffect } from 'react';
import { Eye, FileText, Briefcase } from 'lucide-react';
import { format } from 'date-fns';
import { getRecentGenerations } from '../../utils/firebase/generations';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { CopyButton } from '../ui/CopyButton';
import { EditableLetter } from '../letter/EditableLetter';
import { Modal } from '../ui/Modal';
import type { Generation } from '../../../types/generation';

interface GenerationHistoryProps {
  onLetterSelect?: (content: string, id: string) => void;
}

export function GenerationHistory({ onLetterSelect }: GenerationHistoryProps) {
  const { user } = useAuth();
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContent, setSelectedContent] = useState<{
    content: string;
    type: string;
    jobDescription: string;
  } | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    let isMounted = true;

    const loadGenerations = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const recentGenerations = await getRecentGenerations({ 
          limit: page * itemsPerPage 
        });

        if (isMounted) {
          // Group generations by job description
          const groupedGenerations = recentGenerations.reduce((acc, gen) => {
            const key = gen.jobDescription;
            if (!acc[key]) {
              acc[key] = {
                jobDescription: key,
                timestamp: gen.timestamp,
                items: {}
              };
            }
            acc[key].items[gen.type] = gen;
            return acc;
          }, {} as Record<string, any>);

          const generationsList = Object.values(groupedGenerations);
          setGenerations(generationsList);
          setHasMore(recentGenerations.length === page * itemsPerPage);
        }
      } catch (err) {
        console.error('Error loading generations:', err);
        if (isMounted) {
          setError('Failed to load generation history');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadGenerations();
    return () => {
      isMounted = false;
    };
  }, [user, page]);

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  const handleOpenContent = (content: string, type: string, jobDescription: string) => {
    setSelectedContent({ content, type, jobDescription });
  };

  const handleCloseModal = () => {
    setSelectedContent(null);
  };

  if (!user) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Please sign in to view your generation history</p>
      </div>
    );
  }

  if (isLoading && page === 1) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-red-600 text-center py-4">
        {error}
        <button
          onClick={() => window.location.reload()}
          className="ml-2 text-primary hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (generations.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No application kits generated yet</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Column Headers */}
        <div className="grid grid-cols-[2fr,1fr,1fr,1fr] gap-4 items-center bg-gray-50 p-4 rounded-lg">
          <div className="font-medium">Job Title</div>
          <div className="text-center font-medium">Cover Letter</div>
          <div className="text-center font-medium">Resume</div>
          <div className="text-center font-medium">Job Recommendations</div>
        </div>

        {/* Generation Items */}
        {generations.map((group, index) => {
          // Get job title from recommendations if available
          let jobTitle = '';
          if (group.items.recommendation) {
            try {
              const recommendationData = JSON.parse(group.items.recommendation.content);
              jobTitle = recommendationData.jobTitle;
            } catch (e) {
              console.error('Error parsing recommendation data:', e);
            }
          }

          return (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6">
              <div className="grid grid-cols-[2fr,1fr,1fr,1fr] gap-4 items-center">
                {/* Job Title Column */}
                <div>
                  <h3 className="font-medium text-lg">{jobTitle || 'Application Kit'}</h3>
                  <p className="text-sm text-gray-500">
                    {format(new Date(group.timestamp), 'MMM dd, yyyy HH:mm')}
                  </p>
                </div>

                {/* Cover Letter Column */}
                <div className="text-center">
                  {group.items.letter ? (
                    <button
                      onClick={() => handleOpenContent(
                        group.items.letter.content,
                        'Cover Letter',
                        group.jobDescription
                      )}
                      className="inline-flex items-center px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Open
                    </button>
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </div>

                {/* Resume Column */}
                <div className="text-center">
                  {group.items.resume ? (
                    <button
                      onClick={() => handleOpenContent(
                        group.items.resume.content,
                        'Optimized Resume',
                        group.jobDescription
                      )}
                      className="inline-flex items-center px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Open
                    </button>
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </div>

                {/* Job Recommendations Column */}
                <div className="text-center">
                  {group.items.recommendation ? (
                    <button
                      onClick={() => handleOpenContent(
                        group.items.recommendation.content,
                        'Job Recommendations',
                        group.jobDescription
                      )}
                      className="inline-flex items-center px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
                    >
                      <Briefcase className="w-4 h-4 mr-2" />
                      Open
                    </button>
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Load More Button */}
        {hasMore && (
          <div className="text-center pt-4">
            <button
              onClick={handleLoadMore}
              disabled={isLoading}
              className="px-6 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>

      {/* Content Viewing Modal */}
      <Modal isOpen={!!selectedContent} onClose={handleCloseModal}>
        {selectedContent && (
          <div className="max-w-4xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{selectedContent.type}</h2>
            </div>
            
            <div className="bg-white rounded-lg p-6">
              {selectedContent.type === 'Job Recommendations' ? (
                <JobRecommendationsView content={selectedContent.content} />
              ) : (
                <EditableLetter
                  content={selectedContent.content}
                  jobDescription={selectedContent.jobDescription}
                />
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

function JobRecommendationsView({ content }: { content: string }) {
  try {
    const data = JSON.parse(content);
    return (
      <div className="space-y-4">
        <div>
          <h3 className="font-medium mb-2">Job Title</h3>
          <p>{data.jobTitle}</p>
        </div>
        <div>
          <h3 className="font-medium mb-2">Available Positions</h3>
          <p>{data.stats.count}</p>
        </div>
        <div>
          <h3 className="font-medium mb-2">Job Board Links</h3>
          <div className="space-y-2">
            {Object.entries(data.stats.urls).map(([board, url]) => (
              <a
                key={board}
                href={url as string}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-primary hover:underline"
              >
                {board.charAt(0).toUpperCase() + board.slice(1)}
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  } catch (error) {
    return <div className="text-red-600">Error parsing recommendations data</div>;
  }
}